import type { QueryClient } from '@tanstack/react-query';

import { I18nProvider } from '@lingui/react';
import { TanStackDevtools } from '@tanstack/react-devtools';
import { formDevtoolsPlugin } from '@tanstack/react-form-devtools';
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools';
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  redirect,
} from '@tanstack/react-router';
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools';
import { Toaster } from 'sonner';

import type { AuthContext } from '@/utils/session/session-query-options';

import { CookieBanner } from '@/components/cookie-banner/cookie-banner';
import { BASE_PATH } from '@/constants/config';
import { queryKeys } from '@/hooks/api/query-keys';
import { useHydrated } from '@/hooks/use-hydrated';
import { i18n } from '@/i18n';
import { AnalyticsProvider } from '@/lib/analytics/analytics-provider';
import { ConsentProfileSync } from '@/lib/consent/consent-profile-sync';
import { ConsentProvider } from '@/lib/consent/consent-provider';
import { getConsentRegion } from '@/lib/consent/get-consent-region';
import { getPolyfillGateInlineScript } from '@/polyfills/polyfill-gate';
import { ThemeProvider } from '@/providers/theme-provider';
import { useIsPublicApplyRoute } from '@/routes/participants/-hooks/use-is-public-apply-route';
import {
  buildLegacyParticipantRedirectUrl,
  shouldRedirectParticipantSessionToLegacy,
} from '@/utils/participants-legacy-redirect';
import { getCurrentSessionOptions } from '@/utils/session/session-query-options';

import appCss from '../globals.css?url';

/** Inlined in SSR HTML via `head().scripts` / `<HeadContent />`, not the app body tree. */
const POLYFILL_GATE_HEAD_SCRIPT = getPolyfillGateInlineScript();

// Initialize core-api client with baseURL (must be imported early)
import '@/utils/client-init';
import { initDayJSPlugins } from '@/utils/date';
initDayJSPlugins();

interface RouterContext extends AuthContext {
  queryClient: QueryClient;
  consentRegionCode?: string | null;
}

declare module '@tanstack/react-router' {
  interface HistoryState {
    previousRoute?: string;
  }
}

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context, location }) => {
    // Session is cached client-side so navigations skip a server round trip.
    // Region is cached for the tab: it is the current request's country and
    // does not change mid-session. Both must be available before gtag config.
    //
    // Neither may reject: this is the root route, so a rejection here means no
    // route gets a context and the whole site 500s. The region `catch` also
    // covers transport-level RPC failures, which never reach the server fn's
    // own catch; `null` is "unknown region" => strict consent defaults.
    const [session, consentRegionCode] = await Promise.all([
      context.queryClient.ensureQueryData(getCurrentSessionOptions),
      context.queryClient.ensureQueryData({
        queryKey: queryKeys.consent.region(),
        queryFn: () =>
          getConsentRegion().catch((error) => {
            console.error('Consent region lookup failed:', error);
            return null;
          }),
        staleTime: 1000 * 60 * 60,
      }),
    ]);

    // Legacy participant bounce. Reads the same cached session as every guard
    // below it rather than issuing its own `getServerSession()` — two
    // independent reads of "am I signed in?" in the same request can disagree
    // and turn a transient failure into a redirect loop.
    if (session.env.WEBSITE_URL.length > 0) {
      // If we have a path-only URL, we need to resolve it to a full URL, or it will throw an error
      // We only consume pathname and search anyway, so we can use a dummy base URL.
      const resolved = new URL(location.href, 'https://respondent.invalid');

      const legacyUrl = buildLegacyParticipantRedirectUrl({
        websiteUrl: session.env.WEBSITE_URL,
        pathname: resolved.pathname,
        search: resolved.search,
      });

      if (
        legacyUrl &&
        session.user &&
        // v3Enabled is set at login/signup/verify-email (core-api feature flag there only — not here).
        shouldRedirectParticipantSessionToLegacy({
          user: session.user,
          v3Enabled: session.v3Enabled,
        })
      ) {
        throw redirect({ href: legacyUrl });
      }
    }

    return { ...session, consentRegionCode };
  },
  head: () => ({
    meta: [
      {
        charSet: 'utf8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
      {
        title: 'Respondent',
      },
    ],
    links: [
      // Preconnect to Google Fonts for faster font loading
      {
        rel: 'preconnect',
        href: 'https://fonts.googleapis.com',
      },
      {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
        crossOrigin: 'anonymous',
      },
      // Load Inter font
      {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
      },
      {
        rel: 'stylesheet',
        href: appCss,
      },
      {
        rel: 'icon',
        href: `${BASE_PATH}/favicon.ico`,
      },
    ],
    scripts: [
      {
        type: 'text/javascript',
        children: POLYFILL_GATE_HEAD_SCRIPT,
      },
    ],
  }),
  component: RootDocument,
});

function RootDocument() {
  // Sets `data-hydrated` attribute so e2e tests can reliably wait for
  // hydration and avoid race conditions due to SSR when running locally
  const hydrated = useHydrated();

  return (
    <I18nProvider i18n={i18n}>
      <ThemeProvider defaultTheme="light">
        <html lang="en">
          <head>
            <HeadContent />
          </head>
          <body data-hydrated={hydrated || undefined}>
            <script async src="https://risk.clearbit.com/v1/risk.js" />
            <AppLayout />
            {/* data-react-aria-top-layer: while a React Aria modal (e.g. the
                project-details drawer) is open, everything outside it is made
                inert — clicks on body-level toasts would be swallowed as
                "outside interactions" and their Dismiss buttons dead. The
                attribute tells react-aria to treat the toast region as part
                of the top layer: it stays visible to assistive tech and
                remains clickable without dismissing the modal. */}
            <div data-react-aria-top-layer="true">
              <Toaster position="top-right" />
            </div>
            <TanStackDevtools
              config={{
                hideUntilHover: true,
                position: 'bottom-left',
                inspectHotkey: ['Alt', 'Shift'],
              }}
              plugins={[
                formDevtoolsPlugin(),
                { name: 'TanStack Query', render: <ReactQueryDevtoolsPanel /> },
                {
                  name: 'TanStack Router',
                  render: <TanStackRouterDevtoolsPanel />,
                },
              ]}
            />
            <Scripts />
          </body>
        </html>
      </ThemeProvider>
    </I18nProvider>
  );
}

function AppLayout() {
  const { user, profile, env, activeDashboard, consentRegionCode } =
    Route.useRouteContext();
  const isPublicApply = useIsPublicApplyRoute();
  const organizationId = activeDashboard.organization;
  return (
    /*
      Consent is resolved above analytics so the adapters can publish the
      stored signals before their first event. Whether the prompt appears at
      all is `ConsentProvider`'s call — see `consent-prompt-policy.ts` for the
      iframe / public-apply / local rules. Suppressing the prompt never
      records a decision; it just leaves consent at whatever `rio_consent`
      says, which is deny-all when there is no cookie.

      Local is gated on an exact `local` match rather than IS_LOCAL, which
      also covers `development` and so can be a deployed stage (same reasoning
      as `isLocalStackOrigin` in src/middleware/csrf.ts).
    */
    <ConsentProvider
      profile={profile}
      isSignedIn={user !== null}
      regionCode={consentRegionCode ?? undefined}
      isPublicApplyRoute={isPublicApply}
      isLocalEnvironment={env.ENVIRONMENT === 'local'}
    >
      <AnalyticsProvider
        apiKey={env.POSTHOG_API_KEY}
        googleAnalyticsMeasurementId={env.GOOGLE_ANALYTICS_MEASUREMENT_ID}
        googleTagManagerContainerId={env.GOOGLE_TAG_MANAGER_CONTAINER_ID}
        segmentWriteKey={env.SEGMENT_WRITE_KEY}
        firstPartyWriteKey={env.FIRST_PARTY_ANALYTICS_WRITE_KEY}
        firstPartyHost={env.FIRST_PARTY_ANALYTICS_HOST}
        environment={env.ENVIRONMENT}
        user={user}
        organizationId={organizationId}
        teamId={activeDashboard.team}
      >
        <ConsentProfileSync user={user} profile={profile} />
        <Outlet />
        <CookieBanner />
      </AnalyticsProvider>
    </ConsentProvider>
  );
}
